<template>
    <div v-if="!error.length">
        <AppFooter
            v-if="footer"
            :data="footer"
            :payment-icons
            :social-networks
        />

        <IntercomLauncher />

        <SpeakToExpertModal />

        <EnquiryForm />

        <ContactUsForm />

        <CookieBanner
            :is-visible="!cookiesDismissedCookie"
            @dismiss="dismissCookiesBanner"
        />

        <RegionModal
            :show="showRegionModal"
            :suggested-region="data?.regionSuggest"
            @select-region="hasRegionSet = true"
        />

        <TooltipCmsViewer />

        <ConsultantWarning
            v-if="!hideGeneralWarningsCookie && isAdmin && user"
            :user
            @hide="hideConsultationWarning"
        />

        <div
            v-qa="'app-content-region'"
            v-qa.has.region.modal="showRegionModal"
            v-qa.suggested.region="data?.regionSuggest?.code"
        ></div>
    </div>
</template>
<script setup lang="ts">
import {
    computed,
    defineAsyncComponent,
    hydrateOnIdle,
    hydrateOnVisible,
    onServerPrefetch,
} from 'vue'
import ContactUsForm from '@/components/enquiry/ContactUsForm.vue'
import CookieBanner from '@/components/CookieBanner.vue'
import EnquiryForm from '@/components/enquiry/EnquiryForm.vue'
import SpeakToExpertModal from '@/components/app/speak-to-expert-modal/SpeakToExpertModal.vue'
import RegionModal from '@/components/RegionModal.vue'
import { useIsAdmin } from '@/components/app/isAdmin.ts'
import { useAuth } from '@/plugins/auth.ts'
import {
    makeCookie,
    useCookiesDismissedCookie,
    useHideGeneralWarningsCookie,
} from '@/plugins/cookie-plugin.ts'
import { useMenus } from '@/plugins/menu.ts'
import { getRoutingRegion, useRegionPlugin } from '@/plugins/region.ts'
import { type Link, useHead } from '@unhead/vue'
import type { Time } from '@/types/time.ts'
import type {
    OpeningHoursSpecification,
    PostalAddress,
} from '@unhead/schema-org'
import { assertNever } from '@/helpers'
import {
    defineLocalBusiness,
    defineWebSite,
    useSchemaOrg,
} from '@unhead/schema-org/vue'
import { useAbsoluteUrl } from '@/composables/app/useAbsoluteUrl.ts'
import { useRouter } from 'vue-router'
import { useHost } from '@/plugins/current-url.ts'
import { useTranslator } from '@/i18n'
import { useRoutes } from '@/router/routes.ts'
import type { AlternateTagFragment, RegionCode } from '@/graphql/api'
import { useInit } from '@/plugins/init/use-init.ts'
import { useResponseError } from '@/helpers/errors'

const AppFooter = defineAsyncComponent({
    loader: () => import('@/components/layout/footer/AppFooter.vue'),
    hydrate: hydrateOnVisible(),
})

const TooltipCmsViewer = defineAsyncComponent({
    loader: () => import('@/directives/tooltip/TooltipCmsViewer.vue'),
    hydrate: hydrateOnVisible(),
})

const ConsultantWarning = defineAsyncComponent({
    loader: () => import('@/components/ConsultantWarning.vue'),
    hydrate: hydrateOnIdle(5000),
})

const IntercomLauncher = defineAsyncComponent({
    loader: () => import('@/components/app/intercom/IntercomLauncher.vue'),
    hydrate: hydrateOnIdle(5000),
})

const isAdmin = useIsAdmin()
const { user } = useAuth()
const absoluteUrl = useAbsoluteUrl()
const { regionCode } = useRegionPlugin()
const router = useRouter()
const routes = useRoutes()
const host = useHost()
const { locale } = useTranslator()

const cookiesDismissedCookie = useCookiesDismissedCookie()
const hideGeneralWarningsCookie = useHideGeneralWarningsCookie()

const hideConsultationWarning = () => {
    hideGeneralWarningsCookie.value = makeCookie(true, {
        expires: new Date(new Date().getTime() + 15 * 60000),
    })
}

const dismissCookiesBanner = () => {
    cookiesDismissedCookie.value = true
}

const { footer, paymentIcons, socialNetworks } = useMenus()
const { hasRegionSet } = useRegionPlugin()

const init = useInit()

if (import.meta.env.SSR) {
    onServerPrefetch(async () => {
        await init.promise
    })
} else {
    await init.promise
}

const { initQueryResponse, showRegionModal } = init

const { data } = initQueryResponse

const generateTag = (region: AlternateTagFragment, hreflang: string): Link => {
    // @ts-expect-error because I CBA
    const route = router.resolve({
        ...router.currentRoute.value,
        params: {
            ...router.currentRoute.value.params,
            region: getRoutingRegion(region.code),
        },
    })

    return {
        rel: 'alternate',
        hreflang,
        href: absoluteUrl(route).href,
    }
}

const alternateTags = computed((): Link[] => {
    const tags: Link[] = []

    if (data.value?.defaultAlternateRegion) {
        tags.push(generateTag(data.value?.defaultAlternateRegion, 'x-default'))
    }

    tags.push(
        ...(data.value?.regions
            .filter((region) => region.isActive)
            .map((region) =>
                region.locales.map((locale) => generateTag(region, locale))
            )
            .flat() || [])
    )

    return tags
})

const canonical = computed((): Link => {
    return {
        rel: 'canonical',
        href: absoluteUrl(
            // @ts-expect-error because I CBA
            router.resolve({
                ...router.currentRoute.value,
                params: router.currentRoute.value.params,
                // removes Google Merchant Center id
                query: undefined,
            })
        ).href,
    }
})

useHead({
    link: computed(() => [canonical.value, ...alternateTags.value]),
    templateParams: {
        schemaOrg: {
            host: host.toString(),
            path: router.currentRoute.value.path,
            inLanguage: locale.value,
        },
    },
})

const name = 'Taylor & Hart' as const

const apiTimeToSchemaOrgTime = (
    time: Time
): `${number}${number}:${number}${number}` => {
    return `${Number(time[0] ?? 0)}${Number(time[1] ?? 0)}:${Number(time[3] ?? 0)}${Number(time[4] ?? 0)}`
}

const getAddress = (region: RegionCode): PostalAddress | undefined => {
    switch (region) {
        case 'UK':
            return {
                streetAddress: 'Taylor & Hart, 69 Monmouth Street',
                postalCode: 'WC2H 9DG',
                addressCountry: 'GB',
                addressLocality: 'London',
            }
        case 'US':
            return {
                streetAddress: '55 5th Avenue',
                postalCode: 'NY 1003',
                addressCountry: 'US',
                addressLocality: 'New York',
            }
        case 'AU':
            return undefined
        case 'CA':
            return undefined
        case 'NZ':
            return undefined
        case 'INT':
            return undefined
        case 'EU':
            return undefined
        case 'IE':
            return undefined
        case 'ZA':
            return undefined
        default:
            return assertNever(region, undefined)
    }
}

useSchemaOrg([
    defineLocalBusiness({
        name,
        logo: absoluteUrl('/src/assets/taylor-and-hart-logo.svg').href,
        sameAs: [
            'https://www.instagram.com/taylorandhart',
            'https://www.pinterest.com/taylorandhart',
            'https://www.facebook.com/taylorandhartjeweller',
            'https://twitter.com/taylorandhart',
            'https://www.youtube.com/channel/UC2O2p4q7Zl4sqH6llNxUfbw',
        ],
        priceRange: '$$$',
        telephone: '+442031264915',
        email: 'enquiries@taylorandhart.com',
        openingHoursSpecification: computed((): OpeningHoursSpecification[] => {
            return (
                data.value?.workingHours.map((item) => {
                    return {
                        opens: apiTimeToSchemaOrgTime(item.opening),
                        closes: apiTimeToSchemaOrgTime(item.closing),
                        dayOfWeek: item.weekDays.map((item) => {
                            return `${item.charAt(0)}${item.slice(1).toLowerCase()}` as
                                | 'Friday'
                                | 'Monday'
                                | 'PublicHolidays'
                                | 'Saturday'
                                | 'Sunday'
                                | 'Thursday'
                                | 'Tuesday'
                                | 'Wednesday'
                        }),
                    }
                }) || []
            )
        }),
        address: getAddress(regionCode.value),
    }),
    defineWebSite({
        url: absoluteUrl(routes.home()).href,
        name,
    }),
])

const error = useResponseError(initQueryResponse)
</script>
